import Vue from 'vue'
import VueRouter from 'vue-router'
import Cookie from 'js-cookie'
import store from '../store';
import { getMapFallbackRoute, getMapRouteOrFallback, getRescanRequiredRoute, shouldForceRescan } from '../utils/mapFeature';
import { clearSessionForRescan } from '../utils/session';
import { setPostLoginRedirect } from '../utils/postLoginRedirect';

const Layout = () => import(/* webpackChunkName: "layout" */ '../views/Layout')
const Error = () => import(/* webpackChunkName: "error" */ '../views/errors/Error')
const Login = () => import(/* webpackChunkName: "auth" */ '../views/auth/Login')
const CodeVerify = () => import(/* webpackChunkName: "auth" */ '../views/auth/CodeVerify.vue')
const EmailCodeVerify = () => import(/* webpackChunkName: "auth" */ '../views/auth/EmailCodeVerify.vue')
const PopUpDirectly = () => import(/* webpackChunkName: "auth" */ '../views/auth/PopUpDirectly.vue')
const RegisterInfo = () => import(/* webpackChunkName: "auth" */ '../views/auth/RegisterInfo.vue')
const Profile = () => import(/* webpackChunkName: "auth" */ '../views/auth/Profile.vue')
const PaymentSelect = () => import(/* webpackChunkName: "payment" */ "../views/payments/PaymentSelect.vue")
const RepaymentSelect = () => import(/* webpackChunkName: "payment" */ "../views/payments/RepaymentSelect.vue")
const GatewayRedirect = () => import(/* webpackChunkName: "payment" */ "../views/payments/GatewayRedirect.vue")
const MoyasarRedirect = () => import(/* webpackChunkName: "payment" */ "../views/payments/MoyasarRedirect.vue")
const PaymentResult = () => import(/* webpackChunkName: "payment" */ "../views/payments/PaymentResult.vue")
const LandingPage = () => import(/* webpackChunkName: "dashboard" */ '../views/dashboard/LandingPage.vue')
const BorrowSuccess = () => import(/* webpackChunkName: "rental" */ '../views/rentals/BorrowSuccess.vue')
const MachineConfigError = () => import(/* webpackChunkName: "error" */ '../views/errors/MachineConfigError.vue')
const RentingPage = () => import(/* webpackChunkName: "rental" */ '../views/rentals/RentingPage.vue')
const OrderInfo = () => import(/* webpackChunkName: "rental" */ '../views/rentals/OrderInfo.vue')
const CabinetMap = () => import(/* webpackChunkName: "rental" */ '../views/rentals/CabinetMap.vue')
const Help = () => import(/* webpackChunkName: "help" */ '../views/help/Help.vue')
const ReportProblem = () => import(/* webpackChunkName: "help" */ '../views/help/ReportProblem.vue')
const ChatWithUs = () => import(/* webpackChunkName: "help" */ '../views/help/ChatWithUs.vue')

Vue.use(VueRouter)


const routes = [
  { path: '/error', name: 'error', component: Error, meta: { public: true } },
  { path: '/login', name: 'login', component: Login, meta: { public: true } },
  { path: '/code_verify', name: 'code_verify', component: CodeVerify, meta: { public: true } },
  { path: '/email_code_verify', name: 'email_code_verify', component: EmailCodeVerify, meta: { public: true } },
  { path: '/pop_up_directly', name: 'pop_up_directly', component: PopUpDirectly, meta: { public: true } },
  {
    path: '',
    name: 'layout',
    component: Layout,
    redirect: 'landing_page',
    children: [
      { path: 'register_info', name: 'register_info', component: RegisterInfo, meta: { allow: true } },
      { path: 'profile', name: 'profile', component: Profile, meta: { allow: true } },
      { path: 'payment_select', name: 'payment_select', component: PaymentSelect, meta: { allow: true } },
      { path: 'payment_result', name: 'payment_result', component: PaymentResult, meta: { allow: true } },
      { path: 'payment_redirect/:channel/:internalId', name: 'payment_redirect', component: GatewayRedirect, meta: { allow: true } },
      { path: 'gateway_redirect/:pathMatch(.*)*', name: 'gateway_redirect', component: GatewayRedirect, meta: { allow: true } },
      { path: 'moyasar_redirect/:internalId?', name: 'moyasar_redirect', component: MoyasarRedirect, meta: { allow: true } },
      { path: 'bind_redirect/:pathMatch(.*)*', name: 'bind_redirect', component: GatewayRedirect, meta: { allow: true } },
      { path: 'repayment_select', name: 'repayment_select', component: RepaymentSelect, meta: { allow: true } },
      {
        path: 'recharge_redirect/:pathMatch(.*)*',
        name: 'recharge_redirect',
        component: GatewayRedirect,
        meta: { allow: true }
      },
      { path: 'landing_page', name: 'landing_page', component: LandingPage, meta: { allow: true } },
      { path: 'borrow_success', name: 'borrow_success', component: BorrowSuccess, meta: { allow: true } },
      { path: 'machine_config_error', name: 'machine_config_error', component: MachineConfigError, meta: { allow: true, public: true } },
      { path: 'renting_page', name: 'renting_page', component: RentingPage, meta: { allow: true } },
      { path: 'order_info', name: 'order_info', component: OrderInfo, meta: { allow: true } },
      { path: 'cabinet_map', name: 'cabinet_map', component: CabinetMap, meta: { allow: true, public: true } },
      { path: 'help', name: 'help', component: Help, meta: { allow: true, public: true } },
      { path: 'report_problem', name: 'report_problem', component: ReportProblem, meta: { allow: true } },
      { path: 'chat_with_us', name: 'chat_with_us', component: ChatWithUs, meta: { allow: true, public: true } },
    ]
  }
]

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes
})

// 从 router 中动态获取所有路由路径
const ALL_ROUTES = router.options.routes
  .flatMap(r => r.children ? [r.path, ...r.children.map(c => `/${c.path}`)] : [r.path])
  .filter(Boolean)

// 所有路由路径
export { ALL_ROUTES }

// 免登录可访问路径
const PUBLIC_ROUTES = router.options.routes
  .flatMap(r => r.children ? [r, ...r.children] : [r])
  .filter(r => r.meta?.public)
  .map(r => r.path.startsWith('/') ? r.path : `/${r.path}`)

function getMachineUnavailableMapRoute(routeState) {
    return getMapRouteOrFallback(routeState)
}

function getMissingCodeMapRoute(routeState) {
    return getMapRouteOrFallback(routeState)
}

router.beforeEach((to, from, next) => {
    const token = Cookie.get('token')
    const isBorrow = store.getters.is_borrow
    const machinePrecheckError = store.getters['appConfig/machine_precheck_error']
    const deviceCode = store.getters['appConfig/device_code']
    const mapEnabled = store.getters['appConfig/map_enabled']
    const supportRoutes = ['/profile', '/help', '/report_problem', '/chat_with_us']
    const isSupportRoute = supportRoutes.includes(to.path)
    const isMoyasarRedirectRoute = to.path.startsWith('/moyasar_redirect')
    const routeState = {
        mapEnabled,
        isBorrow,
        deviceCode,
        machinePrecheckError,
    }

    if (shouldForceRescan({ mapEnabled, deviceCode, path: to.path })) {
        if (token) {
            clearSessionForRescan(store)
        }
        const rescanRoute = getRescanRequiredRoute()
        if (to.path !== rescanRoute.path || String(to.query.code || '') !== String(rescanRoute.query.code || '')) {
            next(rescanRoute)
            return
        }
    }

    const isMachineErrorEntryPaths = ['/login', '/machine_config_error', '/code_verify', '/email_code_verify']
    if (mapEnabled) {
        isMachineErrorEntryPaths.push('/cabinet_map')
    }
    const isMachineErrorEntryPath = isMachineErrorEntryPaths.includes(to.path)

    if (to.path === '/cabinet_map' && !mapEnabled) {
        const fallbackRoute = getMapFallbackRoute(routeState)
        if (fallbackRoute.path !== to.path) {
            next(fallbackRoute)
            return
        }
    }

    if (machinePrecheckError && to.path !== '/machine_config_error' && !isMachineErrorEntryPath && !isSupportRoute && !token) {
        next({
            path: '/machine_config_error',
            query: {
                code: machinePrecheckError.device_code,
                errorCode: machinePrecheckError.code,
                message: machinePrecheckError.message,
            }
        })
        return
    }

    // 全局错误处理
    if (!!store.getters['appConfig/error_flag']) {
        if (to.path !== '/error') {
            next({ path: '/error' });
            return;
        }
        next();
        return;
    } else {
        // 如果没有 error_flag，但用户手动访问 /error 且带了 code 参数，允许访问
        if (to.path === '/error' && to.query.code) {
            next();
            return;
        }
    }

    // 直接弹宝模式
    if (store.getters['appConfig/app_mode'] === 1 && to.path !== '/pop_up_directly') {
        next({ path: '/pop_up_directly' })
        return
    }

    if (token) {
        // 有租借订单 → 强制去 renting_page，地图页除外
        if (isBorrow && !isSupportRoute && !isMoyasarRedirectRoute && to.path !== '/renting_page' && !(mapEnabled && to.path === '/cabinet_map')) {
            next({ path: '/renting_page' })
            return
        }

        if (!isBorrow && machinePrecheckError && !isSupportRoute && !isMoyasarRedirectRoute) {
            const machineRoute = getMachineUnavailableMapRoute(routeState)
            if (to.path !== machineRoute.path) {
                next(machineRoute)
                return
            }
        }

        if (!isBorrow && !machinePrecheckError && ['/login', '/machine_config_error'].includes(to.path)) {
            const targetRoute = deviceCode ? { path: '/payment_select' } : getMissingCodeMapRoute(routeState)
            if (targetRoute.path !== to.path) {
                next(targetRoute)
                return
            }
        }

        if (!isBorrow && !machinePrecheckError && to.path === '/payment_select' && !deviceCode) {
            const missingCodeRoute = getMissingCodeMapRoute(routeState)
            if (missingCodeRoute.path !== to.path) {
                next(missingCodeRoute)
                return
            }
        }

        // 没租借 → 只能访问 allow 标记的路由
        const isAllowed = to.matched.some(record => record.meta.allow);
        if (!isBorrow && !isAllowed) {
            next({ path: '/landing_page' })
            return
        }
    } else {
        // 未登录 → 只能访问 public 标记的路由
        const isPublic = to.matched.some(record => record.meta.public);
        if (!isPublic) {
            if (to.path === '/report_problem') {
                setPostLoginRedirect({ path: to.path, query: to.query || {} })
                next({ path: '/login', query: { loginRequired: '1' } })
                return
            }
            next('/login')
            return
        }
    }

    next();
});

export default router
